import {
  createApiFactory,
  createApiRef,
  createComponentExtension,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { rootRouteRef } from './routes';
import { ActivityClient, activityApiRef } from './api/ActivityApi';
import { ActivityAnalyticsApi } from './api/ActivityAnalyticsApi';

/**
 * Held under its own ref as well as the analytics one, because the recorder
 * needs to reach the same instance to log signing in and out — neither of
 * which the analytics API can see.
 */
export const activityAnalyticsApiRef = createApiRef<ActivityAnalyticsApi>({
  id: 'plugin.activity.analytics',
});

export const activityPlugin = createPlugin({
  id: 'activity',
  routes: { root: rootRouteRef },
  apis: [
    createApiFactory({
      api: activityApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new ActivityClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: activityAnalyticsApiRef,
      deps: { activityApi: activityApiRef },
      factory: ({ activityApi }) => ActivityAnalyticsApi.create({ activityApi }),
    }),
  ],
});

/** The full trail, mounted at /activity. */
export const ActivityPage = activityPlugin.provide(
  createRoutableExtension({
    name: 'ActivityPage',
    component: () =>
      import('./components/ActivityPage').then(m => m.ActivityPage),
    mountPoint: rootRouteRef,
  }),
);

/** What has happened to the entity being viewed. For entity pages. */
export const EntityActivityCard = activityPlugin.provide(
  createComponentExtension({
    name: 'EntityActivityCard',
    component: {
      lazy: () =>
        import('./components/EntityActivityCard').then(
          m => m.EntityActivityCard,
        ),
    },
  }),
);

/** A person's own trail. For the user entity page. */
export const EntityUserActivityContent = activityPlugin.provide(
  createComponentExtension({
    name: 'EntityUserActivityContent',
    component: {
      lazy: () =>
        import('./components/UserActivityContent').then(
          m => m.UserActivityContent,
        ),
    },
  }),
);

/**
 * Records signing in and signing out. Renders nothing — it is mounted once,
 * near the root of the app.
 */
export const ActivitySessionRecorder = activityPlugin.provide(
  createComponentExtension({
    name: 'ActivitySessionRecorder',
    component: {
      lazy: () =>
        import('./components/SessionRecorder').then(m => m.SessionRecorder),
    },
  }),
);
